import api from './api';
import { normalizeItemNotas } from '../utils/formatters';
import {
  estadoDesdeRegistroPedido,
  estadosPedidoCoinciden,
  normalizeEstadoPedidoMaster,
} from '../utils/pedidoVentaEstados';

export interface VentaPendienteMaster {
  nroFormularioMaster: number;
  nroItemMaster: number;
  nroCliente?: number;
  nombreCliente?: string;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  nroTipoFormularioMaster?: string | number | null;
  letraComprobanteMaster?: string;
  nroSucComprobanteMaster?: number;
  nroComprobanteMaster?: number | null;
  fechaEmisionComprobanteMaster?: string | null;
  cantidadPedida: number;
  cantidadFacturada: number;
  cantidadRemitida: number;
  cantidadCancelada: number;
  cantidadDevuelta: number;
  /** Órdenes de devolución (tipo 45); reduce el pendiente a facturar. */
  cantidadOrdenDevolucion?: number;
  pendienteFacturar: number;
  pendienteEntregar: number;
  precioListaMaster?: number | null;
  precioUnitarioMaster?: number | null;
  porcDesc1Master?: number | null;
  porcDesc2Master?: number | null;
  porcDesc3Master?: number | null;
  porcDesc4Master?: number | null;
  porcDesc5Master?: number | null;
  notas?: string;
  /** Descripción/observaciones de cabecera del pedido origen. */
  descripcionPedido?: string;
  /** Estado del comprobante master (pedido): Nuevo, Preparado, etc. */
  estadoMaster?: string | null;
  /** Stock.Invetariado (1 = inventariado). */
  invetariado?: number | null;
}

export interface VentaHiloComprobante {
  nroFormulario: number;
  nroItem: number;
  nroTipoFormulario?: string | number;
  fechaEmision?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad: number;
}

export interface VentaHiloResponse {
  nroFormularioMaster: number;
  nroItemMaster: number;
  comprobantes: VentaHiloComprobante[];
}

export function abrevTipoComprobanteVentaMaster(tipo?: string | number | null): string {
  const key = tipo === null || tipo === undefined || tipo === '' ? '' : String(tipo).trim();
  switch (key) {
    case '12':
      return 'PED';
    case '18':
      return 'OT';
    case '9':
      return 'REM';
    case '1':
    case '2':
      return 'FC';
    case '3':
      return 'NC';
    case '45':
      return 'OD';
    default:
      return '';
  }
}

function padComprobanteNum(n: number | null | undefined, len: number): string {
  if (n == null || Number.isNaN(Number(n))) {
    return ''.padStart(len, '0');
  }
  const v = Math.trunc(Number(n));
  return String(Math.max(0, v)).padStart(len, '0');
}

export function formatComprobanteVentaMasterDisplay(p: VentaPendienteMaster): string {
  const tipoAbrev = abrevTipoComprobanteVentaMaster(p.nroTipoFormularioMaster);
  const letraRaw = (p.letraComprobanteMaster ?? '').trim();
  const letra = letraRaw || 'X';
  const suc = p.nroSucComprobanteMaster;
  const nro = p.nroComprobanteMaster;

  const sinDatos =
    !tipoAbrev &&
    !letraRaw &&
    (suc == null || suc === undefined) &&
    (nro == null || nro === undefined);
  if (sinDatos) return '-';

  const cuerpo = `${letra}-${padComprobanteNum(suc, 5)}-${padComprobanteNum(nro, 8)}`;
  return tipoAbrev ? `${tipoAbrev} ${cuerpo}` : cuerpo;
}

export function formatComprobanteVentaHiloLine(c: VentaHiloComprobante): string {
  const tipoAbrev = abrevTipoComprobanteVentaMaster(c.nroTipoFormulario);
  const letra = (c.letra ?? '').trim() || 'X';
  const cuerpo = `${letra}-${padComprobanteNum(c.nroSuc, 5)}-${padComprobanteNum(c.nroComprobante, 8)}`;
  if (tipoAbrev) return `${tipoAbrev} ${cuerpo}`;
  if (c.nroTipoFormulario !== undefined && c.nroTipoFormulario !== null && String(c.nroTipoFormulario).trim() !== '') {
    return `[${c.nroTipoFormulario}] ${cuerpo}`;
  }
  return cuerpo;
}

export function cantidadSugeridaDesdePendienteVenta(p: VentaPendienteMaster): number {
  const pf = Number(p.pendienteFacturar) || 0;
  const pe = Number(p.pendienteEntregar) || 0;
  const m = Math.max(pf, pe);
  return m > 0 ? m : 1;
}

/** Entregado vía remitos (tipo 9). */
export function cantidadEntregadaPendiente(p: VentaPendienteMaster): number {
  return Number(p.cantidadRemitida) || 0;
}

/** Devuelto: órdenes de devolución (45) + notas de crédito (3). */
export function cantidadDevueltaPendiente(p: VentaPendienteMaster): number {
  return (Number(p.cantidadOrdenDevolucion) || 0) + (Number(p.cantidadDevuelta) || 0);
}

export function filtrarPendientesPorFechaEmision<T extends VentaPendienteMaster>(
  rows: T[],
  fechaDesde: string,
  fechaHasta: string,
): T[] {
  const desde = fechaDesde ? new Date(`${fechaDesde}T00:00:00`) : null;
  const hasta = fechaHasta ? new Date(`${fechaHasta}T23:59:59`) : null;
  if (!desde && !hasta) {
    return rows;
  }
  return rows.filter((r) => {
    if (!r.fechaEmisionComprobanteMaster) {
      return false;
    }
    const fecha = new Date(r.fechaEmisionComprobanteMaster);
    if (desde && fecha < desde) {
      return false;
    }
    if (hasta && fecha > hasta) {
      return false;
    }
    return true;
  });
}

export function filtrarPendientesPorArticulo<T extends VentaPendienteMaster>(
  rows: T[],
  busqueda: string,
): T[] {
  const term = busqueda.trim().toLowerCase();
  if (!term) {
    return rows;
  }
  return rows.filter((r) => {
    const codigo = (r.codigoArticulo ?? '').toLowerCase();
    const descripcion = (r.descripcionArticulo ?? '').toLowerCase();
    return codigo.includes(term) || descripcion.includes(term);
  });
}

export function filtrarPendientesPorEstado<T extends VentaPendienteMaster>(
  rows: T[],
  estadoFiltro: string,
): T[] {
  const filtro = estadoFiltro.trim();
  if (!filtro) {
    return rows;
  }
  return rows.filter((r) => estadosPedidoCoinciden(r.estadoMaster, filtro));
}

export function esProductoInventariado(invetariado?: number | null): boolean {
  return Number(invetariado) === 1;
}

type VentaPendienteMasterRaw = VentaPendienteMaster & {
  notasMaster?: string | null;
  Notas?: string | null;
  NotasMaster?: string | null;
  EstadoMaster?: string | null;
  Estado?: string | null;
  estado?: string | null;
};

function pickNotasPendienteRaw(raw: Record<string, unknown>): string | undefined {
  const keys = ['notas', 'notasMaster', 'Notas', 'NotasMaster'];
  for (const key of keys) {
    const val = raw[key];
    if (val != null && String(val).trim() !== '') {
      return normalizeItemNotas(String(val));
    }
  }
  return undefined;
}

function pickEstadoMasterPendienteRaw(raw: Record<string, unknown>): string | undefined {
  return estadoDesdeRegistroPedido(raw);
}

function mapPendienteMasterItem(raw: VentaPendienteMasterRaw): VentaPendienteMaster {
  const record = raw as unknown as Record<string, unknown>;
  const notas = pickNotasPendienteRaw(record);
  const estadoRaw = pickEstadoMasterPendienteRaw(record);
  const estadoMaster = estadoRaw ? normalizeEstadoPedidoMaster(estadoRaw) : undefined;
  return { ...raw, notas, estadoMaster };
}

const ventasSeguimientoService = {
  async getPendientesMaster(nroCliente?: number, soloPendientes = true): Promise<VentaPendienteMaster[]> {
    const response = await api.get<{ items: VentaPendienteMasterRaw[] }>('/api/VentasSeguimiento/pendientes-master', {
      params: {
        nroCliente: nroCliente || undefined,
        soloPendientes,
      },
    });
    return (response.data.items || []).map(mapPendienteMasterItem);
  },

  async getHilo(nroFormularioMaster: number, nroItemMaster: number): Promise<VentaHiloResponse> {
    const response = await api.get<VentaHiloResponse>(
      `/api/VentasSeguimiento/hilo/${nroFormularioMaster}/${nroItemMaster}`
    );
    return response.data;
  },
};

export default ventasSeguimientoService;
